import Events from '../events'
import EventBus from '../event-bus'
import { PushDataLayerEvent } from '../core'
import logger from '@/lib/logger'
import { ntc } from '@/lib/helpers/name-to-color'
import { LogoTracker, LTEvents } from '~/lib/events/tracker'
import { normalizeString } from '~/lib/constants/patterns'
import { getPaletteEventSlug, getPaletteHexValues } from '@/lib/helpers/logos'

EventBus.$on(Events.NotificationClicked, name => {
  PushDataLayerEvent({ event: 'notification_clicked', notification_type: name })
})

EventBus.$on(Events.DomainSelected, domain => {
  PushDataLayerEvent({
    event: 'domain_tld_selected',
    tld: domain.tld,
  })

  LogoTracker.track(LTEvents.DOMAIN_SELECTED, {
    tld: domain.tld.replace('.', ''),
    name: normalizeString(domain.value),
  })
})

EventBus.$on(Events.LayoutMounted, () => {
  PushDataLayerEvent({ event: 'optimize.activate' })
})

EventBus.$on(Events.PageViewed, () => {
  PushDataLayerEvent({ event: 'optimize.activate' })
})

EventBus.$on(Events.PageViewed, () => {
  LogoTracker.track(LTEvents.PAGE_VIEWED)
})

EventBus.$on(Events.AppVisit, () => {
  PushDataLayerEvent({ event: 'app_visit' })
})

EventBus.$on(Events.ModalOpen, ({ name }) => {
  PushDataLayerEvent({
    event: 'modal_open',
    modal_type: `modal_${name}`,
  })

  PushDataLayerEvent({ event: `modal_${name}_open` })
})

EventBus.$on(Events.ModalClose, ({ name }) => {
  PushDataLayerEvent({ event: `modal_${name}_close` })
})

EventBus.$on(Events.LogoSaved, ({ logo }) => {
  const { assets } = logo
  const icon = assets.icons[0]
  const wordmarkFont = assets.wordmark_fonts[0]
  const sloganFont = assets.slogan_fonts[0]

  const templateName = assets.template.name

  const { palette } = assets
  const { primary } = palette

  const colorVals = getPaletteHexValues(palette)

  const wordmarkColor = getPaletteEventSlug(primary)
  const backgroundColor = ntc.name(colorVals.background)[3]
  const iconId = icon ? icon.id : null
  const wordmarkFontName = wordmarkFont ? wordmarkFont.name : null
  const sloganFontName = sloganFont ? sloganFont.name : null
  const { shape } = assets.template

  const wordmarkScriptConfig = assets?.template?.wordmark_script || {}

  const payload = {
    event: 'logo_saved',
    template_name: templateName,
    wordmark_color: wordmarkColor,
    background_color: backgroundColor,
    icon_id: iconId,
    wordmark_font_name: wordmarkFontName,
    slogan_font_name: sloganFontName,
    shape_name: shape,
  }

  PushDataLayerEvent(payload)

  LogoTracker.track(LTEvents.LOGO_CREATED, {
    wordmark: logo.wordmark,
    template_id: templateName,
    wordmark_color: wordmarkColor,
    background_color: backgroundColor,
    wordmark_font: wordmarkFontName,
    slogan_font: sloganFontName || null,
    icon_id: iconId || null,
    shape: shape || null,
    wordmark_script_text: wordmarkScriptConfig.text || null,
    wordmark_script_type: wordmarkScriptConfig.format || 'none',
    wordmark_script_font: wordmarkScriptConfig.font || 'none',
    wordmark_script_shape: wordmarkScriptConfig.shape || 'none',
  })
})

EventBus.$on(Events.ProgressStepCompleted, ({ step }) => {
  PushDataLayerEvent({
    event: 'progress_step_completed',
    progress_step: `step_${step.slug}`,
  })
})

EventBus.$on(Events.AllProgressStepsCompleted, () => {
  PushDataLayerEvent({
    event: 'progress_step_completed',
    progress_step: 'step_all_completed',
  })
})

EventBus.$on(Events.ProgressBarNavigated, () => {
  PushDataLayerEvent({ event: 'button_clicked', button_type: 'progress_bar' })
})

/**
 * Use for generic GA4 event tracking.
 *
 * @param {{ event: string }} payload the data to track.
 */
EventBus.$on(Events.AnalyticsGA4Event, payload => {
  PushDataLayerEvent(payload)
})

EventBus.$on(Events.FileDownloaded, ({ name }) => {
  PushDataLayerEvent({ event: 'file_downloaded', file_name: name })
})

EventBus.$on(Events.LogoShareMenuOpened, () => {
  PushDataLayerEvent({ event: 'button_clicked', button_type: 'share_logo' })
})

EventBus.$on(Events.FavoritesMenuOpened, () => {
  PushDataLayerEvent({ event: 'button_clicked', button_type: 'favorites' })
})

EventBus.$on(Events.EmailSignatureSaved, ({ slug }) => {
  PushDataLayerEvent({
    event: 'button_clicked',
    button_type: 'email_signature',
  })

  LogoTracker.track(LTEvents.SIGNATURE_DOWNLOADED, {
    template: slug,
  })
})

/**
 * Use for manual search event tracking.
 *
 * @param {{ search_term: string, type: string }} payload the data to track.
 */
EventBus.$on(Events.AnalyticsSearch, payload => {
  logger.debug(`Tracking GA4 search event:`, payload)

  PushDataLayerEvent({ event: 'search', ...payload })
})

/**
 * Use to track the selection of every asset in a Logo.
 *
 * @param {{ logo: object, list: string }} payload the data to track.
 */
EventBus.$on(Events.AnalyticsSelectLogo, ({ logo, list }) => {
  const { wordmark_fonts, slogan_fonts, template, palette, icons } = logo.assets

  // The list of logo assets to track.
  const items = [
    { type: 'design_alignment', id: template?.orientation },
    { type: 'background_shape', id: template?.shape },
    { type: 'font', id: wordmark_fonts?.[0]?.id },
    { type: 'font', id: slogan_fonts?.[0]?.id },
    { type: 'template', id: logo.template },
    { type: 'palette', id: palette?.id },
    { type: 'icon', id: icons?.[0]?.id },
  ]

  items.forEach(item => {
    // Only track selected assets
    if (item.id) {
      EventBus.$emit(Events.EcommerceSelectContent, { ...item, list })
    }
  })
  try {
    LogoTracker.track(LTEvents.LOGO_SELECTED, {
      logo_id: logo.id,
      list,
      orientation: template.orientation,
      shape: template.shape,
      wordmark_font_id: wordmark_fonts[0]?.id,
      slogan_font_id: slogan_fonts[0]?.id,
      template_id: logo.template,
      palette_id: palette.id,
      icon_id: icons?.[0]?.id || null,
    })
  } catch (e) {
    console.log(e)
  }
})
